import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { cs, type MessageKey } from './cs';
import { en } from './en';
import type { TranslateFn } from './types';

export type { MessageKey, TranslateFn };

/**
 * i18n vrstva (pozadavky.md kap. 6) — všechny texty UI procházejí přes `t()`.
 * Přepínač jazyka v UI přidá Epic 11 (E11-US8); tady je jen vrstva a stav.
 * Systémové výstupy z DB (hlášky driverů, názvy typů) se **nepřekládají**.
 */
export type Language = 'cs' | 'en';

const CATALOGS: Record<Language, Record<MessageKey, string>> = { cs, en };
const STORAGE_KEY = 'bits2schema.language';
const DEFAULT_LANGUAGE: Language = 'cs';

interface I18nContextValue {
  language: Language;
  setLanguage: (language: Language) => void;
  t: TranslateFn;
}

const I18nContext = createContext<I18nContextValue | null>(null);

function readStoredLanguage(): Language {
  const stored = window.localStorage.getItem(STORAGE_KEY);
  return stored === 'cs' || stored === 'en' ? stored : DEFAULT_LANGUAGE;
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<Language>(readStoredLanguage);

  const setLanguage = useCallback((next: Language) => {
    window.localStorage.setItem(STORAGE_KEY, next);
    document.documentElement.lang = next;
    setLanguageState(next);
  }, []);

  const t = useCallback<TranslateFn>(
    (key, params) => {
      const template = CATALOGS[language][key] ?? key;
      if (!params) return template;
      return template.replace(/\{(\w+)\}/g, (match, name: string) =>
        name in params ? String(params[name]) : match,
      );
    },
    [language],
  );

  const value = useMemo<I18nContextValue>(
    () => ({ language, setLanguage, t }),
    [language, setLanguage, t],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nContextValue {
  const context = useContext(I18nContext);
  if (!context) throw new Error('useI18n musí být uvnitř I18nProvider.');
  return context;
}

/** Zkratka pro komponenty, které potřebují jen překladovou funkci. */
export function useT(): TranslateFn {
  return useI18n().t;
}
